import React, { useState } from 'react';
import { BarChartIcon, TrendingUpIcon, ShoppingCartIcon, UsersIcon, PackageIcon, ArrowUpIcon, ArrowDownIcon, DollarSignIcon, CalendarIcon, InfoIcon } from 'lucide-react';
import { Shop } from '../pages/Shops';
interface ShopDashboardProps {
  shop: Shop;
}
export function ShopDashboard({
  shop
}: ShopDashboardProps) {
  const [hoveredRevenue, setHoveredRevenue] = useState<{
    month: string;
    revenue: number;
  } | null>(null);
  const [hoveredOrder, setHoveredOrder] = useState<{
    month: string;
    orders: number;
  } | null>(null);
  // Create more pronounced seasonal pattern for a beach shop
  // Higher sales in summer months, peak in July-August, lower in winter
  const seasonalPattern = [0.25, 0.3, 0.45, 0.6, 0.75, 0.9, 1.0, 0.95, 0.7, 0.5, 0.35, 0.4];
  // Generate revenue data with more pronounced seasonal patterns
  const revenueData = [{
    month: 'Jan',
    revenue: Math.round(shop.revenue.total * seasonalPattern[0])
  }, {
    month: 'Feb',
    revenue: Math.round(shop.revenue.total * seasonalPattern[1])
  }, {
    month: 'Mar',
    revenue: Math.round(shop.revenue.total * seasonalPattern[2])
  }, {
    month: 'Apr',
    revenue: Math.round(shop.revenue.total * seasonalPattern[3])
  }, {
    month: 'May',
    revenue: Math.round(shop.revenue.total * seasonalPattern[4])
  }, {
    month: 'Jun',
    revenue: Math.round(shop.revenue.total * seasonalPattern[5])
  }, {
    month: 'Jul',
    revenue: Math.round(shop.revenue.total * seasonalPattern[6])
  }, {
    month: 'Aug',
    revenue: Math.round(shop.revenue.total * seasonalPattern[7])
  }, {
    month: 'Sep',
    revenue: Math.round(shop.revenue.total * seasonalPattern[8])
  }, {
    month: 'Oct',
    revenue: Math.round(shop.revenue.total * seasonalPattern[9])
  }, {
    month: 'Nov',
    revenue: Math.round(shop.revenue.total * seasonalPattern[10])
  }, {
    month: 'Dec',
    revenue: Math.round(shop.revenue.total * seasonalPattern[11])
  }];
  // Generate orders data with the same seasonal pattern
  const ordersData = [{
    month: 'Jan',
    orders: Math.round(shop.ordersCount * seasonalPattern[0])
  }, {
    month: 'Feb',
    orders: Math.round(shop.ordersCount * seasonalPattern[1])
  }, {
    month: 'Mar',
    orders: Math.round(shop.ordersCount * seasonalPattern[2])
  }, {
    month: 'Apr',
    orders: Math.round(shop.ordersCount * seasonalPattern[3])
  }, {
    month: 'May',
    orders: Math.round(shop.ordersCount * seasonalPattern[4])
  }, {
    month: 'Jun',
    orders: Math.round(shop.ordersCount * seasonalPattern[5])
  }, {
    month: 'Jul',
    orders: Math.round(shop.ordersCount * seasonalPattern[6])
  }, {
    month: 'Aug',
    orders: Math.round(shop.ordersCount * seasonalPattern[7])
  }, {
    month: 'Sep',
    orders: Math.round(shop.ordersCount * seasonalPattern[8])
  }, {
    month: 'Oct',
    orders: Math.round(shop.ordersCount * seasonalPattern[9])
  }, {
    month: 'Nov',
    orders: Math.round(shop.ordersCount * seasonalPattern[10])
  }, {
    month: 'Dec',
    orders: Math.round(shop.ordersCount * seasonalPattern[11])
  }];
  const topProducts = [{
    name: 'Beach Towel',
    sales: 38,
    revenue: 1139.62
  }, {
    name: 'Sunglasses',
    sales: 27,
    revenue: 1079.73
  }, {
    name: 'Swim Shorts',
    sales: 24,
    revenue: 839.76
  }, {
    name: 'Beach Sandals',
    sales: 22,
    revenue: 549.78
  }];
  const customerStats = {
    total: 287,
    new: 42,
    returning: 245,
    conversionRate: 3.8
  };
  // Calculate max values for chart scaling
  const maxRevenue = Math.max(...revenueData.map(item => item.revenue));
  const maxOrders = Math.max(...ordersData.map(item => item.orders));
  return <div className="space-y-6" data-id="element-2077">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2078">
        <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center" data-id="element-2079">
          <BarChartIcon size={18} className="mr-2 text-blue-600" data-id="element-2080" />
          Shop Dashboard
        </h2>
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-2081">
          {/* Revenue KPI */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-2082">
            <div className="flex justify-between items-start" data-id="element-2083">
              <div data-id="element-2084">
                <p className="text-sm text-gray-500 mb-1" data-id="element-2085">Total Revenue</p>
                <h3 className="text-2xl font-bold text-gray-800" data-id="element-2086">
                  Rs. {shop.revenue.total.toLocaleString('en-LK')}
                </h3>
              </div>
              <div className="p-2 bg-blue-50 rounded-md" data-id="element-2087">
                <DollarSignIcon size={20} className="text-blue-600" data-id="element-2088" />
              </div>
            </div>
            <div className="mt-2 flex items-center" data-id="element-2089">
              <span className={`inline-flex items-center text-xs font-medium ${shop.revenue.growth >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-2090">
                {shop.revenue.growth >= 0 ? <ArrowUpIcon size={12} className="mr-1" data-id="element-2091" /> : <ArrowDownIcon size={12} className="mr-1" data-id="element-2092" />}
                {Math.abs(shop.revenue.growth)}%
              </span>
              <span className="text-xs text-gray-500 ml-1" data-id="element-2093">vs last month</span>
            </div>
          </div>
          {/* Orders KPI */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-2094">
            <div className="flex justify-between items-start" data-id="element-2095">
              <div data-id="element-2096">
                <p className="text-sm text-gray-500 mb-1" data-id="element-2097">Total Orders</p>
                <h3 className="text-2xl font-bold text-gray-800" data-id="element-2098">
                  {shop.ordersCount}
                </h3>
              </div>
              <div className="p-2 bg-purple-50 rounded-md" data-id="element-2099">
                <ShoppingCartIcon size={20} className="text-purple-600" data-id="element-2100" />
              </div>
            </div>
            <div className="mt-2 flex items-center" data-id="element-2101">
              <span className="inline-flex items-center text-xs font-medium text-green-600" data-id="element-2102">
                <ArrowUpIcon size={12} className="mr-1" data-id="element-2103" />
                12.4%
              </span>
              <span className="text-xs text-gray-500 ml-1" data-id="element-2104">vs last month</span>
            </div>
          </div>
          {/* Products KPI */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-2105">
            <div className="flex justify-between items-start" data-id="element-2106">
              <div data-id="element-2107">
                <p className="text-sm text-gray-500 mb-1" data-id="element-2108">Total Products</p>
                <h3 className="text-2xl font-bold text-gray-800" data-id="element-2109">
                  {shop.productsCount}
                </h3>
              </div>
              <div className="p-2 bg-green-50 rounded-md" data-id="element-2110">
                <PackageIcon size={20} className="text-green-600" data-id="element-2111" />
              </div>
            </div>
            <div className="mt-2 flex items-center" data-id="element-2112">
              <span className="inline-flex items-center text-xs font-medium text-green-600" data-id="element-2113">
                <ArrowUpIcon size={12} className="mr-1" data-id="element-2114" />
                5.2%
              </span>
              <span className="text-xs text-gray-500 ml-1" data-id="element-2115">vs last month</span>
            </div>
          </div>
          {/* Customers KPI */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-2116">
            <div className="flex justify-between items-start" data-id="element-2117">
              <div data-id="element-2118">
                <p className="text-sm text-gray-500 mb-1" data-id="element-2119">Total Customers</p>
                <h3 className="text-2xl font-bold text-gray-800" data-id="element-2120">
                  {customerStats.total}
                </h3>
              </div>
              <div className="p-2 bg-orange-50 rounded-md" data-id="element-2121">
                <UsersIcon size={20} className="text-orange-600" data-id="element-2122" />
              </div>
            </div>
            <div className="mt-2 flex items-center" data-id="element-2123">
              <span className="inline-flex items-center text-xs font-medium text-green-600" data-id="element-2124">
                <ArrowUpIcon size={12} className="mr-1" data-id="element-2125" />
                8.7%
              </span>
              <span className="text-xs text-gray-500 ml-1" data-id="element-2126">vs last month</span>
            </div>
          </div>
        </div>
        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-2127">
          {/* Revenue Chart - Completely revamped implementation */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-2128">
            <div className="flex justify-between items-center mb-4" data-id="element-2129">
              <h4 className="text-sm font-medium text-gray-700" data-id="element-2130">
                Revenue Trend
              </h4>
              <div className="flex items-center text-xs text-gray-500" data-id="element-2131">
                <CalendarIcon size={14} className="mr-1" data-id="element-2132" />
                Last 12 months
              </div>
            </div>
            <div className="relative h-60 mb-6" data-id="element-2133">
              {/* Tooltip */}
              {hoveredRevenue && <div className="absolute top-0 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 z-10" data-id="element-2134">
                  {hoveredRevenue.month}: Rs.{' '}
                  {hoveredRevenue.revenue.toLocaleString('en-LK')}
                </div>}
              {/* Y-axis labels */}
              <div className="absolute top-0 left-0 h-full flex flex-col justify-between text-xs text-gray-500 pr-2" data-id="element-2135">
                <div data-id="element-2136">Rs. {maxRevenue.toLocaleString('en-LK')}</div>
                <div data-id="element-2137">
                  Rs. {Math.round(maxRevenue * 0.75).toLocaleString('en-LK')}
                </div>
                <div data-id="element-2138">
                  Rs. {Math.round(maxRevenue * 0.5).toLocaleString('en-LK')}
                </div>
                <div data-id="element-2139">
                  Rs. {Math.round(maxRevenue * 0.25).toLocaleString('en-LK')}
                </div>
                <div data-id="element-2140">Rs. 0</div>
              </div>
              {/* Chart area */}
              <div className="absolute left-12 right-0 h-full flex items-end" data-id="element-2141">
                {/* Horizontal grid lines */}
                <div className="absolute inset-0" data-id="element-2142">
                  <div className="border-t border-gray-100 h-1/4" data-id="element-2143"></div>
                  <div className="border-t border-gray-100 h-1/4" data-id="element-2144"></div>
                  <div className="border-t border-gray-100 h-1/4" data-id="element-2145"></div>
                  <div className="border-t border-gray-100 h-1/4" data-id="element-2146"></div>
                </div>
                {/* Chart bars */}
                <div className="relative w-full h-full flex items-end" data-id="element-2147">
                  {revenueData.map((item, index) => {
                  const barHeight = `${item.revenue / maxRevenue * 100}%`;
                  return <div key={index} className="flex-1 flex flex-col items-center justify-end h-full" onMouseEnter={() => setHoveredRevenue(item)} onMouseLeave={() => setHoveredRevenue(null)} data-id="element-2148">
                        <div style={{
                      height: barHeight
                    }} className={`w-4/5 ${index === 11 ? 'bg-blue-600' : 'bg-blue-500 hover:bg-blue-600'} rounded-t transition-all duration-200`} data-id="element-2149"></div>
                      </div>;
                })}
                </div>
              </div>
              {/* X-axis labels */}
              <div className="absolute bottom-0 left-12 right-0 flex justify-between text-xs text-gray-500 pt-2" data-id="element-2150">
                {revenueData.map((item, index) => <div key={index} className="text-center" data-id="element-2151">
                    {item.month}
                  </div>)}
              </div>
            </div>
            <div className="text-xs text-gray-500 text-center" data-id="element-2152">
              <span className="inline-flex items-center" data-id="element-2153">
                <InfoIcon size={12} className="mr-1" data-id="element-2154" />
                Beach shop revenue shows seasonal patterns with summer peak
              </span>
            </div>
          </div>
          {/* Orders Chart - Completely revamped implementation */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-2155">
            <div className="flex justify-between items-center mb-4" data-id="element-2156">
              <h4 className="text-sm font-medium text-gray-700" data-id="element-2157">
                Orders Trend
              </h4>
              <div className="flex items-center text-xs text-gray-500" data-id="element-2158">
                <CalendarIcon size={14} className="mr-1" data-id="element-2159" />
                Last 12 months
              </div>
            </div>
            <div className="relative h-60 mb-6" data-id="element-2160">
              {/* Tooltip */}
              {hoveredOrder && <div className="absolute top-0 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 z-10" data-id="element-2161">
                  {hoveredOrder.month}: {hoveredOrder.orders.toLocaleString()}{' '}
                  orders
                </div>}
              {/* Y-axis labels */}
              <div className="absolute top-0 left-0 h-full flex flex-col justify-between text-xs text-gray-500 pr-2" data-id="element-2162">
                <div data-id="element-2163">{maxOrders.toLocaleString()}</div>
                <div data-id="element-2164">{Math.round(maxOrders * 0.75).toLocaleString()}</div>
                <div data-id="element-2165">{Math.round(maxOrders * 0.5).toLocaleString()}</div>
                <div data-id="element-2166">{Math.round(maxOrders * 0.25).toLocaleString()}</div>
                <div data-id="element-2167">0</div>
              </div>
              {/* Chart area */}
              <div className="absolute left-12 right-0 h-full flex items-end" data-id="element-2168">
                {/* Horizontal grid lines */}
                <div className="absolute inset-0" data-id="element-2169">
                  <div className="border-t border-gray-100 h-1/4" data-id="element-2170"></div>
                  <div className="border-t border-gray-100 h-1/4" data-id="element-2171"></div>
                  <div className="border-t border-gray-100 h-1/4" data-id="element-2172"></div>
                  <div className="border-t border-gray-100 h-1/4" data-id="element-2173"></div>
                </div>
                {/* Chart bars */}
                <div className="relative w-full h-full flex items-end" data-id="element-2174">
                  {ordersData.map((item, index) => {
                  const barHeight = `${item.orders / maxOrders * 100}%`;
                  return <div key={index} className="flex-1 flex flex-col items-center justify-end h-full" onMouseEnter={() => setHoveredOrder(item)} onMouseLeave={() => setHoveredOrder(null)} data-id="element-2175">
                        <div style={{
                      height: barHeight
                    }} className={`w-4/5 ${index === 11 ? 'bg-purple-600' : 'bg-purple-500 hover:bg-purple-600'} rounded-t transition-all duration-200`} data-id="element-2176"></div>
                      </div>;
                })}
                </div>
              </div>
              {/* X-axis labels */}
              <div className="absolute bottom-0 left-12 right-0 flex justify-between text-xs text-gray-500 pt-2" data-id="element-2177">
                {ordersData.map((item, index) => <div key={index} className="text-center" data-id="element-2178">
                    {item.month}
                  </div>)}
              </div>
            </div>
            <div className="text-xs text-gray-500 text-center" data-id="element-2179">
              <span className="inline-flex items-center" data-id="element-2180">
                <InfoIcon size={12} className="mr-1" data-id="element-2181" />
                Order volume follows similar seasonal trends as revenue
              </span>
            </div>
          </div>
        </div>
        {/* Additional Insights Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2182">
          {/* Top Products */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-2183">
            <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-2184">
              Top Selling Products
            </h4>
            <div className="space-y-3" data-id="element-2185">
              {topProducts.map((product, index) => <div key={index} className="flex items-center justify-between border-b border-gray-100 pb-3 last:border-0 last:pb-0" data-id="element-2186">
                  <div className="flex items-center" data-id="element-2187">
                    <div className="w-8 h-8 bg-gray-100 rounded-md flex items-center justify-center text-gray-500 mr-3" data-id="element-2188">
                      {index + 1}
                    </div>
                    <div data-id="element-2189">
                      <p className="text-sm font-medium text-gray-800" data-id="element-2190">
                        {product.name}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-2191">
                        {product.sales} sold
                      </p>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-2192">
                    <p className="text-sm font-medium text-gray-800" data-id="element-2193">
                      Rs.{' '}
                      {product.revenue.toLocaleString('en-LK', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-2194">Revenue</p>
                  </div>
                </div>)}
            </div>
          </div>
          {/* Customer Insights */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-2195">
            <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-2196">
              Customer Insights
            </h4>
            <div className="grid grid-cols-2 gap-4" data-id="element-2197">
              <div className="bg-gray-50 rounded-md p-3" data-id="element-2198">
                <p className="text-xs text-gray-500 mb-1" data-id="element-2199">New Customers</p>
                <p className="text-lg font-medium text-gray-800" data-id="element-2200">
                  {customerStats.new}
                </p>
                <div className="mt-1 flex items-center" data-id="element-2201">
                  <span className="text-xs text-green-600 flex items-center" data-id="element-2202">
                    <ArrowUpIcon size={10} className="mr-1" data-id="element-2203" />
                    14.3%
                  </span>
                </div>
              </div>
              <div className="bg-gray-50 rounded-md p-3" data-id="element-2204">
                <p className="text-xs text-gray-500 mb-1" data-id="element-2205">
                  Returning Customers
                </p>
                <p className="text-lg font-medium text-gray-800" data-id="element-2206">
                  {customerStats.returning}
                </p>
                <div className="mt-1 flex items-center" data-id="element-2207">
                  <span className="text-xs text-green-600 flex items-center" data-id="element-2208">
                    <ArrowUpIcon size={10} className="mr-1" data-id="element-2209" />
                    7.2%
                  </span>
                </div>
              </div>
              <div className="bg-gray-50 rounded-md p-3" data-id="element-2210">
                <p className="text-xs text-gray-500 mb-1" data-id="element-2211">Avg. Order Value</p>
                <p className="text-lg font-medium text-gray-800" data-id="element-2212">
                  Rs.{' '}
                  {(shop.revenue.total / shop.ordersCount).toLocaleString('en-LK', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                </p>
                <div className="mt-1 flex items-center" data-id="element-2213">
                  <span className="text-xs text-green-600 flex items-center" data-id="element-2214">
                    <ArrowUpIcon size={10} className="mr-1" data-id="element-2215" />
                    3.5%
                  </span>
                </div>
              </div>
              <div className="bg-gray-50 rounded-md p-3" data-id="element-2216">
                <p className="text-xs text-gray-500 mb-1" data-id="element-2217">Conversion Rate</p>
                <p className="text-lg font-medium text-gray-800" data-id="element-2218">
                  {customerStats.conversionRate}%
                </p>
                <div className="mt-1 flex items-center" data-id="element-2219">
                  <span className="text-xs text-green-600 flex items-center" data-id="element-2220">
                    <ArrowUpIcon size={10} className="mr-1" data-id="element-2221" />
                    1.2%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}